import { ResponsiveTimeRange } from '@nivo/calendar';
import { Radio, Space } from 'antd';
import { CustomEmpty, LoadingIndicator, QueryErrorAlert } from 'components';
import dayjs from 'dayjs';
import { useCustomTranslation } from 'helpers/translationHelper';
import React, { useMemo, useState } from 'react';

import type { DashboardOverviewPoint } from '../../models';
import { useDashboardOverviewQuery } from '../../queries';
import DashboardRangeControls from './DashboardRangeControls';
import type { DashboardRange } from './useDashboardRange';

interface CalendarTabProps {
  range: DashboardRange;
}

/**
 * Metrics the heatmap can show. Each is a single counter straight from the statistics projection —
 * deliberately never a sum of two kinds: adding planned + completed would double-count a dispatch
 * that was planned and completed on the same day, which is what the previous version did.
 */
type CalendarMetric =
  | 'plannedInterventions'
  | 'completedInterventions'
  | 'submittedForms'
  | 'customers'
  | 'orders'
  | 'completedOrders'
  | 'serviceObjects';

const METRICS: readonly CalendarMetric[] = [
  'plannedInterventions',
  'completedInterventions',
  'submittedForms',
  'customers',
  'orders',
  'completedOrders',
  'serviceObjects'
];

const METRIC_LABEL_KEYS: Record<CalendarMetric, string> = {
  plannedInterventions: 'calendarChart_plannedInterventions',
  completedInterventions: 'calendarChart_completeInterventions',
  submittedForms: 'calendarChart_submittedForms',
  customers: 'calendarChart_customers',
  orders: 'calendarChart_orders',
  completedOrders: 'calendarChart_completeOrders',
  serviceObjects: 'calendarChart_serviceObjects'
};

const CalendarTab: React.FC<CalendarTabProps> = ({ range }) => {
  const { tDashboard } = useCustomTranslation();
  const [metric, setMetric] = useState<CalendarMetric>('plannedInterventions');
  const { from, to } = range;
  // Daily values from the Statistics module — day granularity so one cell is one day. The tab shows
  // no granularity picker for that reason; a heatmap cell is a day by construction.
  const overviewQuery = useDashboardOverviewQuery(from, to, 'day');
  const points = overviewQuery.data?.points;

  const data = useMemo(
    () =>
      (points ?? [])
        .map((d: DashboardOverviewPoint) => ({ day: d.period, value: d[metric] }))
        .filter((d) => d.value > 0),
    [points, metric]
  );

  // TimeRange lays the window out as one continuous strip of weeks, so the height is constant
  // regardless of how long the range is — seven weekday rows plus the month and weekday legends.
  const height = 220;

  if (overviewQuery.error) {
    return (
      <div>
        <DashboardRangeControls {...range} showGranularity={false} />
        <QueryErrorAlert error={overviewQuery.error} onRetry={overviewQuery.refetch} />
      </div>
    );
  }
  if (overviewQuery.isFetching || !points) {
    return (
      <div>
        <DashboardRangeControls {...range} showGranularity={false} />
        <LoadingIndicator />
      </div>
    );
  }

  const metricLabel = tDashboard(METRIC_LABEL_KEYS[metric]);

  return (
    <div>
      <DashboardRangeControls {...range} showGranularity={false} />
      <h4>{tDashboard('calendar_dailyHeatmap')}</h4>
      <Space style={{ marginBottom: 16 }} wrap>
        <Radio.Group value={metric} onChange={(e) => setMetric(e.target.value as CalendarMetric)}>
          {METRICS.map((m) => (
            <Radio.Button key={m} value={m}>
              {tDashboard(METRIC_LABEL_KEYS[m])}
            </Radio.Button>
          ))}
        </Radio.Group>
      </Space>

      {/* Spell out what a cell means — the heatmap alone doesn't say whose numbers these are. */}
      <p>{tDashboard('calendar_metricHint', { metric: metricLabel })}</p>

      {data.length === 0 ? (
        <CustomEmpty />
      ) : (
        <div style={{ height }}>
          {/*
            TimeRange, not Calendar: Calendar always draws whole calendar years, so a 12-month
            window rendered two year grids padded with empty months outside the selection.
            TimeRange draws exactly the days between from and to.
          */}
          <ResponsiveTimeRange
            from={from}
            to={to}
            data={data}
            emptyColor="#eeeeee"
            colors={['#61cdbb', '#97e3d5', '#e8c1a0', '#f47560']}
            margin={{ top: 40, right: 40, bottom: 40, left: 40 }}
            // Format from the date, not the month index — nivo passes the index through unchanged
            // for every legend, so deriving the name from it labels every month January.
            monthLegend={(_year, _month, date) => dayjs(date).format('MMM')}
            dayBorderWidth={2}
            dayBorderColor="#ffffff"
            // No weekday rail: nivo hardcodes those labels in English and exposes no way to
            // translate them. The month legend already anchors the strip.
            weekdayTicks={[]}
          />
        </div>
      )}
    </div>
  );
};

export default CalendarTab;
